import { FrequentlyUsed } from 'emoji-mart';

/**
 * Helpers around emoji-mart's frequently-used store.
 *
 * emoji-mart v5 persists usage counts in localStorage under `emoji-mart.frequently`
 * as a flat Record<emojiId, count> (native emoji-mart ids or custom emoji shortnames).
 * All access to that store lives here — reads parse localStorage directly, writes
 * delegate to emoji-mart's own `FrequentlyUsed` (with the single pre-index exception
 * documented on `seedFrequentEmojiDefaults`) — so a format change in a future
 * emoji-mart bump breaks exactly one place and degrades gracefully.
 */
const FREQUENTLY_USED_KEY = 'emoji-mart.frequently';

/**
 * Curated default quick-reaction emojis, most prominent first. Used to top up the
 * quick row before the user has usage history, and to seed the frequently-used
 * store (`seedFrequentEmojiDefaults`) so the picker's Frequent section starts from
 * this set instead of emoji-mart's own hardcoded defaults.
 *
 * Lives here rather than in quick-emojis.ts so that `EmojiMartAdapter` can import
 * it without any import path back into Angular service land.
 */
export const DEFAULT_QUICK_EMOJI_IDS: readonly string[] = [
  '+1',
  'heart',
  'joy',
  'tada',
  'cry',
  'fire',
  'clap',
  'eyes',
  'pray',
];

function readStore(): Record<string, number> | undefined {
  // Everything lives inside the try — localStorage.getItem itself throws in
  // contexts where storage access is blocked (sandboxed iframes, privacy modes),
  // and readStore is reached from computed()s during change detection.
  try {
    const raw = localStorage.getItem(FREQUENTLY_USED_KEY);
    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return undefined;
    }

    const store: Record<string, number> = {};
    for (const [id, count] of Object.entries(parsed)) {
      if (typeof count === 'number' && Number.isFinite(count)) {
        store[id] = count;
      }
    }

    return store;
  } catch {
    return undefined;
  }
}

/**
 * Emoji ids from emoji-mart's frequently-used store, most used first.
 * Returns [] when the store is missing or unreadable.
 */
export function getFrequentlyUsedEmojiIds(): string[] {
  const store = readStore();
  if (!store) {
    return [];
  }

  return Object.entries(store)
    .sort(([, a], [, b]) => b - a)
    .map(([id]) => id);
}

/**
 * Increments the usage count for an emoji id (creating the store/entry if absent),
 * keeping the row and the picker's "Frequently Used" section in sync.
 *
 * Must go through emoji-mart's own `FrequentlyUsed.add` rather than writing
 * localStorage directly: the picker holds the frequently-used index in a
 * module-level cache and rewrites the store wholesale on its next selection,
 * which would silently discard direct writes. `add` also maintains
 * `emoji-mart.last`, which the picker's pruning logic uses to protect the
 * most recent selection.
 *
 * Silently no-ops if the store cannot be read or written.
 */
export function recordFrequentEmojiUsage(id: string): void {
  try {
    FrequentlyUsed.add({ id });
  } catch {
    // Storage full or unavailable — usage tracking is best-effort.
  }
}

/**
 * Seeds the frequently-used store with a curated default set (descending counts,
 * so sort order preserves the given order). No-ops if a readable store already
 * exists — gated on `readStore()`, which treats everything emoji-mart's `Store.get`
 * treats as absent as absent (it is stricter only for corrupt/non-object stores,
 * where overwriting with the seed is a repair).
 *
 * MUST run before emoji-mart's `init()` — NOT merely before the first `Picker`.
 * `init()` itself synchronously calls `FrequentlyUsed.get()` while building the
 * Frequent category, and when it finds no store it seeds emoji-mart's module-level
 * index with emoji-mart's OWN default emojis; the next `FrequentlyUsed.add` — e.g.
 * the user's first quick-reaction click — persists that index, silently and
 * permanently replacing any curated set the app meant to show. `EmojiMartAdapter`'s
 * `ensureInit()` is the single `init()` chokepoint and is where this is called.
 *
 * This is the one place a direct localStorage write is safe: it happens before
 * emoji-mart's index exists, so the index hydrates from this store. Everywhere
 * else writes must go through `FrequentlyUsed.add` (see recordFrequentEmojiUsage).
 * If storage is blocked entirely, the seed no-ops and emoji-mart falls back to its
 * own in-memory defaults for the Frequent section — nothing persists, so that
 * cosmetic divergence is accepted.
 */
export function seedFrequentEmojiDefaults(ids: readonly string[]): void {
  // Seeding an empty set would persist `{}`, permanently suppressing future seeding
  // and making emoji-mart drop its Frequent category outright.
  if (!ids.length) {
    return;
  }

  try {
    if (readStore() !== undefined) {
      return;
    }

    const store: Record<string, number> = {};
    ids.forEach((id, index) => {
      store[id] = ids.length - index;
    });

    localStorage.setItem(FREQUENTLY_USED_KEY, JSON.stringify(store));
  } catch {
    // Storage unavailable — seeding is best-effort.
  }
}
